/**
 * @fileoverview TaskQueueItem component
 * Displays a task in the queue with click interaction to reveal action buttons
 */

import React, { useState } from 'react';
import { Card, CardBody, Button, Spinner, Alert, Badge, Toast, ToastBody } from 'reactstrap';
import TaskTimer from './TaskTimer';
import PDFViewerModal from './Common/PDFViewerModal';

const TaskQueueItem = ({ 
  tarea, 
  onTaskStart, 
  onTaskFinish, 
  loading = false,
  onShowModal
}) => {
  const [error, setError] = useState('');
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showPDFModal, setShowPDFModal] = useState(false);

  const handleTaskClick = () => {
    // Don't show actions for finished tasks
    if (tarea.fin && tarea.fin !== '') return;
    
    // Only allow starting tasks with priority 1
    if (canStart && tarea.prioridad !== 1) return;
    
    setError(''); // Clear any previous errors
    
    // Determine action based on task state
    if (canStart) {
      handleStartTask();
    } else if (canFinish) {
      handleFinishTask();
    }
  };

  const handleStartTask = () => {
    setError('');
    if (onShowModal) {
      onShowModal('iniciar', tarea);
    } else {
      // Fallback to direct action
      handleStartTaskDirect();
    }
  };

  const handleFinishTask = () => {
    setError('');
    if (onShowModal) {
      onShowModal('finalizar', tarea);
    } else {
      // Fallback to direct action
      handleFinishTaskDirect();
    }
  };

  const handleStartTaskDirect = async () => {
    try {
      setError('');
      await onTaskStart(tarea.id);
      setSuccessMessage('¡Tarea iniciada correctamente!');
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);
    } catch (err) {
      setError('Error al iniciar tarea');
      console.error('Error starting task:', err);
    }
  };

  const handleFinishTaskDirect = async () => {
    try {
      setError('');
      await onTaskFinish(tarea.id);
      setSuccessMessage('¡Tarea finalizada correctamente!');
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);
    } catch (err) {
      setError('Error al finalizar tarea');
      console.error('Error finishing task:', err);
    }
  };

  const getTaskStatus = () => {
    if (tarea.fin && tarea.fin !== '') return 'Finalizado';
    if (tarea.inicio && tarea.inicio !== '') return 'En Proceso';
    return 'Listo';
  };

  const getStatusColor = () => {
    if (tarea.fin && tarea.fin !== '') return 'success';
    if (tarea.inicio && tarea.inicio !== '') return 'warning';
    return 'info';
  };

  const getCardStyle = () => {
    if (tarea.fin && tarea.fin !== '') return { borderLeft: '4px solid #28a745' }; // Green for finished
    if (tarea.inicio && tarea.inicio !== '') return { borderLeft: '4px solid #ffc107' }; // Yellow for in progress
    if (tarea.prioridad === 1) return { borderLeft: '4px solid #17a2b8' }; // Blue for priority 1 ready
    return { 
      borderLeft: '4px solid #6c757d', // Gray for non-priority 1
      opacity: 0.6,
      backgroundColor: '#f8f9fa'
    }; // Gray for non-priority 1
  };

  const canStart = tarea.ready && tarea.prioridad === 1 && (!tarea.inicio || tarea.inicio === '');
  const canFinish = Boolean(tarea.inicio && tarea.inicio !== '' && (!tarea.fin || tarea.fin === ''));

  const handleViewPDF = (e) => {
    e.stopPropagation(); // Prevent task click
    setShowPDFModal(true);
  };

  return (
    <>
      <Card 
        className="mb-3 task-queue-item"
        data-testid="task-item"
        role="button"
        tabIndex={0}
        aria-expanded={false}
        onClick={handleTaskClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleTaskClick();
          }
        }}
        style={{ 
          cursor: ((tarea.fin && tarea.fin !== '') || (tarea.ready && tarea.prioridad !== 1 && (!tarea.inicio || tarea.inicio === ''))) ? 'default' : 'pointer',
          ...getCardStyle()
        }}
      >
      <CardBody>
        <div className="d-flex justify-content-between align-items-start">
          <div className="flex-grow-1">
            <div className="d-flex align-items-center mb-2 flex-wrap">
              <Badge color="primary" className="me-2">
                Prioridad: {tarea.prioridad}
              </Badge>
              {tarea.inicio && tarea.inicio !== '' && (!tarea.fin || tarea.fin === '') && (
                <TaskTimer startTime={tarea.inicio} />
              )}
              {(!tarea.fin || tarea.fin === '') && (tarea.prioridad === 1 || canFinish) && (
                <Badge color="info" className="ms-2">
                  <i className="fas fa-hand-pointer me-1"></i>
                  {canStart ? 'Click para iniciar' : canFinish ? 'Click para finalizar' : ''}
                </Badge>
              )}
            </div>
            
            <div className="task-details">
              <p className="mb-1 d-flex align-items-center">
                <strong>Orden:</strong> 
                <span className="ms-1">{tarea.expand?.orden?.numero || 'N/A'}</span>
                {tarea.expand?.orden?.id && (
                  <Button
                    color="outline-primary"
                    size="sm"
                    className="ms-2"
                    onClick={handleViewPDF}
                    title="Ver PDF de la orden"
                  >
                    <i className="fas fa-file-pdf me-1"></i>
                    PDF
                  </Button>
                )}
              </p>
              <p className="mb-1">
                <strong>Producto:</strong> {tarea.expand?.orden?.nombre_producto || 'N/A'}
              </p>
              <p className="mb-1">
                <strong>Centro:</strong> {tarea.expand?.centro_costo?.nombre || 'N/A'}
              </p>
              
              {tarea.inicio && (
                <p className="mb-1 text-muted">
                  <small>Iniciado: {new Date(tarea.inicio).toLocaleString()}</small>
                </p>
              )}
              
              {tarea.fin && (
                <p className="mb-1 text-muted">
                  <small>Finalizado: {new Date(tarea.fin).toLocaleString()}</small>
                </p>
              )}
            </div>
          </div>
          
          {loading && (
            <div className="ms-2" data-testid="loading-spinner">
              <Spinner size="sm" color="primary" />
            </div>
          )}
        </div>

        {error && (
          <Alert color="danger" className="mt-2 mb-0" timeout={0}>
            {error}
          </Alert>
        )}

      </CardBody>
    </Card>

    {/* Success Toast */}
    {showSuccessToast && (
      <div className="position-fixed" style={{ top: '20px', right: '20px', zIndex: 9999 }}>
        <Toast isOpen={showSuccessToast} className="bg-success text-white">
          <ToastBody>
            <i className="fas fa-check-circle me-2"></i>
            {successMessage}
          </ToastBody>
        </Toast>
      </div>
    )}

    {/* PDF Viewer Modal */}
    {tarea.expand?.orden?.id && (
      <PDFViewerModal
        isOpen={showPDFModal}
        toggle={() => setShowPDFModal(false)}
        ordenId={tarea.expand.orden.id}
        titulo={`Orden #${tarea.expand.orden.numero} - ${tarea.expand.orden.nombre_producto}`}
        filename={`orden_produccion_${tarea.expand.orden.numero}.pdf`}
      />
    )}
    </>
  );
};

export default TaskQueueItem;
